import React, { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useCustomEventListener } from "react-custom-events";
import useQuery from "../../hooks/useQuery";
import PaginationView from "../../components/molecules/Pagination";
import { DashboardLayout } from "../../components/organisms/Layout";
import { ModalDeleteWrapper } from "../../components/organisms/ModalDeleteWrapper";
import JobSeeker from "../../api/JobSeeker";
import { toast } from "react-hot-toast";
import { useNavigate, useSearchParams } from "react-router-dom";
import useFormatPhoneNumber from "../../hooks/FormatPhoneNumber";
import { defaultUserImage } from "../../assets/images";
import { encodeIds } from "../../utils";
import Table from "../../components/atoms/Tables/Table";
import IconSelector from "../../components/atoms/Selector/IconSelector";
import SearchBar from "../../components/atoms/SearchBar";
import debounce from "lodash/debounce";

const JobSeekers = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const formatPhoneNumber = useFormatPhoneNumber;
  const query = useQuery();
  const [showModalDelete, setShowModalDelete] = useState(false);
  const [jobSeekers, setJobSeekers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageLimit, setPagelimit] = useState(query.get("pageSize") || 50);
  const [metaData, setMetaData] = useState({});
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(query.get("search") || "");

  useEffect(() => {
    document.title = "HeyHire Portal-Job Seekers";
  }, []);

  useEffect(() => {
    loadJobSeekers();
  }, [query]);

  const debouncedUpdateSearch = useCallback(
    debounce((txt) => {
      const newParams = { ...Object.fromEntries(searchParams) };
      newParams.page = 1;

      if (txt && txt.trim() !== "") {
        newParams.search = txt.trim();
      } else {
        delete newParams.search;
      }

      setSearchParams(newParams);
    }, 300),
    [searchParams]
  );

  const handleSearch = (value) => {
    setSearchQuery(value);
    debouncedUpdateSearch(value);
  };

  const loadJobSeekers = () => {
    setLoading(true);
    const page = query.get("page") || 1;
    const sort = query.get("sort") || "";
    const limit = query.get("limit") || pageLimit;
    const search = query.get("search") || "";

    const sortData = sort ? `sort=${sort}&` : "";
    const pageData = page ? `page=${page}` : "";
    const searchData = search ? `&search=${encodeURIComponent(search)}` : "";
    const queryData = sortData + pageData + `&limit=${limit}` + searchData;

    // Update search input when loading from URL
    setSearchQuery(search);

    JobSeeker.getAll(queryData)
      .then((response) => {
        setJobSeekers(response.data.data);
        setMetaData(response.data.meta);
      })
      .catch((error) => {
        if (error?.response?.status !== 403) {
          toast.error("Something Went Wrong.");
        }
      })
      .then(() => setLoading(false));
  };

  useCustomEventListener("closeModal", () => {
    loadJobSeekers();
  });

  const handlecPageChange = (currentPage, pageSize) => {
    setPagelimit(pageSize);
    setSearchParams({ ...searchParams, page: currentPage, pageSize: pageSize });
  };

  return (
    <DashboardLayout darmo={true}>
      <div className="pb-12">
        <Table
          isLoading={loading}
          mainHeader={t("job-seekers.job-seekers")}
          searchBar={
            <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6 w-full sm:justify-end">
              <div className="w-full sm:w-[500px]">
                <SearchBar
                  searchValue={searchQuery}
                  placeholder="Search by name, email, or location..."
                  searchFunction={handleSearch}
                />
              </div>
            </div>
          }
          maxHeight="calc(100vh - 250px)"
          buttonData={null}
          headers={[
            t("job-seekers.applicant"),
            t("common.phone"),
            t("common.email"),
            `${t("common.city")} / ${t("common.state")}`,
            t("common.number-of-applications"),
            t("common.action"),
          ]}
          emptyState={
            !loading &&
            jobSeekers?.length === 0 && (
              <div className="text-center py-12">
                <svg
                  className="mx-auto h-12 w-12 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                  />
                </svg>
                <h3 className="mt-2 text-sm font-medium text-gray-900">
                  No job seekers found
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  {searchQuery
                    ? "Try adjusting your search terms"
                    : "No job seekers available"}
                </p>
              </div>
            )
          }
          data={jobSeekers?.map((jobSeeker, index) => (
            <tr
              key={index}
              className="hover:bg-gray-50 transition-colors duration-150"
            >
              <td className="whitespace-nowrap px-6 py-3 text-sm">
                <div className="flex items-center">
                  <div className="h-10 w-10 flex-shrink-0">
                    <img
                      alt=""
                      src={jobSeeker?.photo?.thumb_url || defaultUserImage}
                      className="h-10 w-10 rounded-full object-cover ring-2 ring-gray-100"
                    />
                  </div>
                  <div className="ml-3">
                    <div className="font-semibold text-gray-900">
                      {jobSeeker?.first_name} {jobSeeker.last_name}
                    </div>
                  </div>
                </div>
              </td>

              <td className="whitespace-nowrap px-6 py-4 text-sm">
                <div className="text-gray-700">
                  {formatPhoneNumber(jobSeeker.phone) || "-"}
                </div>
              </td>
              <td className="whitespace-nowrap px-6 py-4 text-sm">
                <div className="text-gray-700">
                  {jobSeeker.email}
                </div>
              </td>
              <td className="whitespace-nowrap px-6 py-4 text-sm">
                <div className="text-gray-700">
                  {jobSeeker.city} / {jobSeeker.state}
                </div>
              </td>
              <td className="whitespace-nowrap px-6 py-4 text-sm">
                <div className="text-gray-700">
                  {jobSeeker?.num_applications}
                </div>
              </td>
              <td className="whitespace-nowrap px-2 sm:px-3 py-2.5 sm:py-3 text-sm md:text-base">
                <div className="flex items-center gap-4">
                  <button
                    className="text-primary-600 hover:text-primary-900 font-medium hover:underline"
                    onClick={() =>
                      navigate(`/job-seekers/edit/${encodeIds(jobSeeker.id)}`)
                    }
                  >
                    {t("common.edit")}
                  </button>
                  <button
                    className="text-red-600 hover:text-red-900 font-medium hover:underline"
                    onClick={() => setShowModalDelete(true)}
                  >
                    {t("common.delete")}
                  </button>
                  <IconSelector
                    menus={[
                      {
                        label: "Resend Welcome Email",
                        action: () => { },
                      },
                    ]}
                  />
                </div>
              </td>
            </tr>
          ))}
        />
        <div className="flex w-full h-20 justify-center md:justify-end items-center px-4">
          {!loading && metaData?.total > 0 && (
            <PaginationView
              current={metaData?.current_page}
              next={metaData?.to}
              pageSize={metaData?.per_page}
              total={metaData?.total}
              onChange={handlecPageChange}
            />
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <ModalDeleteWrapper
        visible={showModalDelete}
        onClose={() => setShowModalDelete(false)}
        title={t("common.delete") + " " + t("job-seekers.job-seekers")}
      />
    </DashboardLayout>
  );
};

export default JobSeekers;
